import * as React from 'react';
import {
  Cell,
  CellTemplate,
  Compatible,
  isAlphaNumericKey,
  isNavigationKey,
  getCellProperty,
  getCharFromKeyCode,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from '@silevis/reactgrid';

interface FormattedTextCell extends Cell {
  text: string;
  value: number;
  placeholder: string;
  validator?: (text: string) => boolean;
  className?: string;
  errorMessage?: string;
  renderer?: (text: string) => React.ReactNode;
}

type CompatibleFormattedTextCell = Compatible<FormattedTextCell>;

class AbstracTextTemplate implements CellTemplate<FormattedTextCell> {
  formatValue(value: string) {
    return value;
  }

  getCompatibleCell(
    uncertainCell: Uncertain<FormattedTextCell>,
  ): CompatibleFormattedTextCell {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    let placeholder: string;
    try {
      placeholder = getCellProperty(uncertainCell, 'placeholder', 'string');
    } catch {
      placeholder = '';
    }
    const value = parseFloat(text);

    return {
      ...uncertainCell,
      text,
      value,
      placeholder,
    };
  }

  update(
    cell: CompatibleFormattedTextCell,
    cellToMerge: UncertainCompatible<FormattedTextCell>,
  ): CompatibleFormattedTextCell {
    return this.getCompatibleCell({
      ...cell,
      text: cellToMerge.text,
      placeholder: cellToMerge.placeholder,
    });
  }

  handleKeyDown(
    cell: CompatibleFormattedTextCell,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: CompatibleFormattedTextCell; enableEditMode: boolean } {
    const char = getCharFromKeyCode(keyCode, shift);
    if (
      !ctrl &&
      !alt &&
      isAlphaNumericKey(keyCode) &&
      !(shift && keyCode === keyCodes.SPACE)
    )
      return {
        cell: this.getCompatibleCell({
          ...cell,
          text: shift ? char : char.toLowerCase(),
        }),
        enableEditMode: true,
      };
    return {
      cell,
      enableEditMode:
        keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  getClassName(cell: CompatibleFormattedTextCell) {
    const isValid = cell.validator ? cell.validator(cell.text) : true;
    const className = cell.className ? cell.className : '';
    return `${isValid ? 'valid' : 'invalid'} ${
      cell.placeholder && cell.text === '' ? 'placeholder' : ''
    } ${className}`;
  }

  render(
    cell: CompatibleFormattedTextCell,
    isInEditMode: boolean,
    onCellChanged: (cell: CompatibleFormattedTextCell, commit: boolean) => void,
  ): React.ReactNode {
    if (!isInEditMode) {
      const isValid = cell.validator ? cell.validator(cell.text) : true;
      const cellText = cell.text || cell.placeholder || '';
      const textToDisplay =
        !isValid && cell.errorMessage ? cell.errorMessage : cellText;
      return cell.renderer ? cell.renderer(textToDisplay) : textToDisplay;
    }

    return (
      <input
        ref={(input) => {
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }}
        defaultValue={cell.text}
        onChange={(e) => {
          e.target.value = this.formatValue(e.target.value);
          return onCellChanged(
            this.getCompatibleCell({ ...cell, text: e.currentTarget.value }),
            false,
          );
        }}
        onBlur={(e) =>
          onCellChanged(
            this.getCompatibleCell({ ...cell, text: e.currentTarget.value }),
            true,
          )
        }
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        placeholder={cell.placeholder}
        onKeyDown={(e) => {
          if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode))
            e.stopPropagation();
        }}
      />
    );
  }
}

export default AbstracTextTemplate;
